import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Box from '@mui/material/Box'
import makeStyles from '@mui/styles/makeStyles'
import { take } from 'ramda'
import { type FieldObject, Text } from '@pbt/pbt-ui-components'
import theme from '@pbt/pbt-ui-components/src/muiTheme'
import type { Patient } from '@pbt/pbt-ui-components/src/types'

import PuiDataTable from '~/components/common/lists/pui-data-table/PuiDataTable'
import PuiSwitch from '~/components/common/PuiSwitch'

export const VIEWPORT_ITEMS = 5

const useStyles = makeStyles(() => ({
  header: {
    display: 'none',
  },
  table: {
    border: theme.constants.tableBorder,
    borderBottom: 0,
    borderColor: theme.colors.grayGray3,
  },
  row: {
    backgroundColor: theme.colors.grayWhite,
  },
}))

export function usePatientRemindersToggleTableColumns(
  sendRemindersFields: FieldObject,
  onFieldChange: (fieldName: string, value: boolean) => void,
) {
  const { t } = useTranslation(['Common', 'Dialogs'])

  const getFieldName = (id: string | undefined) => `${id}SendReminders`

  return [
    {
      label: t('Common:NAME'),
      key: 'name',
      value: (patient: Patient) => {
        const fieldName = getFieldName(patient.id)
        const field = sendRemindersFields[fieldName]
        const isChecked = field?.value ?? true

        const handleToggle = () => {
          onFieldChange(fieldName, !isChecked)
        }

        return (
          <Box alignItems="center" display="flex" gap={1}>
            <PuiSwitch checked={isChecked} onChange={handleToggle} />
            <Text>{patient.name}</Text>
          </Box>
        )
      },
    },
  ]
}

export interface PatientRemindersToggleTableProps {
  onFieldChange: (fieldName: string, value: boolean) => void
  patients: Patient[]
  sendRemindersFields: FieldObject
}

export function PatientRemindersToggleTable({
  sendRemindersFields,
  patients,
  onFieldChange,
}: PatientRemindersToggleTableProps) {
  const [expanded, setExpanded] = useState(false)
  const { t } = useTranslation(['Common', 'Dialogs'])
  const classes = useStyles()
  const columns = usePatientRemindersToggleTableColumns(
    sendRemindersFields,
    onFieldChange,
  )

  const viewportItems = expanded
    ? patients.length
    : Math.min(patients.length, VIEWPORT_ITEMS)

  return (
    <Box>
      <Text strong sx={{ my: theme.spacing(1) }} variant="body2">
        {t('Dialogs:NEW_CLIENT_DETAILS_EDIT_DIALOG.REMINDER_SHARING')}
      </Text>
      <Text sx={{ mb: theme.spacing(1) }} variant="body2">
        {t(
          'Dialogs:NEW_CLIENT_DETAILS_EDIT_DIALOG.CARE_DUE_REMINDERS_DESCRIPTION',
        )}
      </Text>
      <PuiDataTable
        densed
        withBorder
        classes={{
          table: classes.table,
        }}
        classesHeader={{
          headingRow: classes.header,
        }}
        classesRow={{
          row: classes.row,
        }}
        columns={columns}
        items={take(viewportItems, patients)}
        loadMoreItems={() => {}}
        rowHeight={40}
        totalCount={viewportItems}
        viewportItems={viewportItems}
      />
      {patients.length > VIEWPORT_ITEMS && (
        <Box
          alignItems="center"
          display="flex"
          sx={{
            cursor: 'pointer',
            p: theme.spacing(1),
            border: theme.constants.tableBorder,
            borderTop: 'none',
            borderColor: theme.colors.grayGray3,
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Text strong variant="body2">
            {expanded
              ? t('Common:LESS')
              : `${t('Dialogs:NEW_CLIENT_DETAILS_EDIT_DIALOG.MORE_PATIENTS')} (${patients.length - viewportItems})`}
          </Text>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </Box>
      )}
    </Box>
  )
}
